import stipulate from 'stipulate';
import urlJoin from 'url-join';
import { baseUrl } from './base';

/**
 * Looks up domain availability for a given search string.
 *
 * @param {Object} options - Options for the request.
 * @param {string} options.apiUrl - Base API URL (e.g. 'https://api.yola.com').
 * @param {string} options.searchString - The domain name or search string to look up (required).
 * @param {string} [options.searchKey] - Optional opaque search key to correlate or resume searches.
 * @param {string} [options.businessCategory] - Optional business category (used to refine suggestions).
 * @param {string} [options.businessDescription] - Optional business description to provide additional context.
 * @param {string} [options.businessName] - Optional business name associated with the lookup.
 * @param {Array} [options.tlds] - Optional list of TLDs to narrow the lookup (e.g. ['com', 'net']).
 * @param {string} [options.siteLocale] - Optional site locale (e.g. 'en-US') to influence localization.
 * @param {AbortSignal} [options.signal] - Optional AbortSignal to cancel the network request.
 * @returns {Promise.<{results: Array.<{domain: string, available: boolean}>, search_key: (string|undefined)}>}
 *   Resolves with an object containing lookup results and an optional search_key.
 */
const lookupDomainsAvailability = function (options) {
  const url = urlJoin(
    options.apiUrl,
    baseUrl,
    '/pb/lookup-domains-availability/'
  );

  const requestBody = { search_string: options.searchString };

  if (options.searchKey) {
    requestBody.search_key = options.searchKey;
  }

  if (options.businessCategory) {
    requestBody.business_category = options.businessCategory;
  }

  if (options.businessDescription) {
    requestBody.business_description = options.businessDescription;
  }

  if (options.businessName) {
    requestBody.business_name = options.businessName;
  }

  if (options.tlds) {
    requestBody.tlds = options.tlds;
  }

  if (options.siteLocale) {
    requestBody.site_locale = options.siteLocale;
  }

  const fetchOptions = {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
    },
    credentials: 'include',
    body: JSON.stringify(requestBody),
  };
  if (options.signal) {
    fetchOptions.signal = options.signal;
  }
  return stipulate(url, fetchOptions);
};

export default lookupDomainsAvailability;
